import currentUser from 'system/currentUser';
import { Fnv } from 'utilities/Fnv';

import { Environment } from 'Environment';
import { BoundWhiteLabelSelectors } from 'reactApp/modules/WhiteLabelModule/WhiteLabelModule';

const TEST_USERS = /^test(\.box|_login)[0-9A-z-+_.]+@(mail|inbox|list|bk)\.ru$/i;

const CORP_USERS = /^[0-9A-z-+_.]+@(corp|biz|mcs|cloud)\.mail\.ru$/i;

const ALPHA_USERS = [
    '1bxtli3',
    '101qo14',
    '11kgtnv',
    '13627nd',
    '13ddt6b',
    '13sez75',
    '14tjvjh',
    '15qytuz',
    '16ut0re',
    '1a3dnsc',
    '1azq6mg',
    '1cozchu',
    '1d47cun',
    '1d4nbrr',
    '1dig24s',
    '1dow7uj',
    '1en8dop',
    '1fhhs8u',
    '1fsus77',
    '1fufset',
    '1fw55s9',
    '1h8k8xs',
    '1har81a',
    '1i3i9nv',
    '1icee40',
    '1ixnemu',
    '1liqkiv',
    '1ljzd3u',
    '1lpjor8',
    '1ncat5m',
    '1nusxgb',
    '1o2c695',
    '1ow4ffk',
    '1pg9oni',
    '1r6dulv',
    '1ru0njo',
    '1tvzt87',
    '1v30bar',
    '1vtssi7',
    '1vzbn2l',
    '1w16qxr',
    '1xr607t',
    '4gmzv5',
    '4hj4hg',
    '4vucdn',
    '5dc8bf',
    '5fmt7u',
    '66w7t9',
    '6bcosh',
    '6usyur',
    '6uvqnf',
    '789r0k',
    '78ccl',
    '78lb99',
    '8uuc8v',
    '9tx6ol',
    'b2d9m',
    'bjyhmq',
    'dnfr1h',
    'im5ie3',
    'ipvhku',
    'jpseu6',
    'laif0k',
    'lsu9si',
    'n6y5ai',
    'ncp7jo',
    'nif275',
    'nxnx6a',
    'ohv1my',
    'pv0boi',
    'pxmxyz',
    'qk9pes',
    'rirdwl',
    'rw4c85',
    'u7lwnf',
    'wa3cyn',
    'xn3jvy',
    'ye0ean',
    'ye1ig8',
    'yygnwb',
    'zhv1ty',
    'el8puu',
    TEST_USERS,
];

const BETA_USERS = [...ALPHA_USERS, CORP_USERS];

const fnv32cache = Object.create(null);

function getCachedInt36Fnv32aHash(email) {
    let hash = fnv32cache[email];

    if (hash) {
        return hash;
    }

    hash = Fnv.getInt36Fnv32aHash(email);
    fnv32cache[email] = hash;

    return hash;
}

/**
 * @param {Array.<string|RegExp>} patterns
 * @param {string} email
 * @returns {boolean}
 */
function match(patterns, email) {
    let isMatches = false;

    for (const pattern of patterns) {
        if (pattern instanceof RegExp) {
            isMatches = pattern.test(email);
        } else if (typeof pattern === 'string') {
            if (pattern.includes('@')) {
                isMatches = pattern === email;
            } else {
                isMatches = pattern === getCachedInt36Fnv32aHash(email);
            }
        }

        if (isMatches) {
            break;
        }
    }

    return isMatches;
}

function getCurrentUserEmail() {
    return currentUser.get('email');
}

function isUserInSegment(segment) {
    return match(segment, getCurrentUserEmail());
}

function isAlphaUser() {
    return isUserInSegment(ALPHA_USERS);
}

function isTestUser() {
    return isUserInSegment([TEST_USERS]);
}

function isBetaUser() {
    return isUserInSegment(BETA_USERS);
}

function checkServiceAvailability({
    serviceAliasForWhiteLabelSettings,
    isAvailableByDefault = true,
    isAvailableForAlphaUsers = true,
    isAvailableForBetaUsers = true,
    canBeAvailableForPartnerClients = true,
    unconditionallyAvailableForUsers = [],
}) {
    if (isAvailableForAlphaUsers && isAlphaUser()) {
        return true;
    }

    if (isAvailableForBetaUsers && isBetaUser()) {
        return true;
    }

    if (isUserInSegment(unconditionallyAvailableForUsers)) {
        return true;
    }

    const whiteLabelSettings = BoundWhiteLabelSelectors.getWhiteLabelSettings();

    if (whiteLabelSettings) {
        if (canBeAvailableForPartnerClients) {
            const { available_services } = whiteLabelSettings;

            if (Array.isArray(available_services)) {
                return available_services.includes(
                    serviceAliasForWhiteLabelSettings,
                );
            }
        }

        return false;
    }

    return isAvailableByDefault;
}

function checkBigDataServiceAvailability() {
    return checkServiceAvailability({
        serviceAliasForWhiteLabelSettings: 'bigdata',
    });
}

const knownFeatures = {
    BALANCE_CHART: true,

    MULTICLOUD() {
        return isUserInSegment([
            '14tjvjh',
            'jpseu6',
            'zhv1ty',
            'multicloud@mcs.mail.ru',
        ]);
    },

    MULTICLOUD_MOCK() {
        return isUserInSegment(['multicloud@mcs.mail.ru']);
    },

    DPP: false,

    SENTRY: !Environment.isPrivateBuild,

    MONGODB: true,

    CLICKHOUSE: true,

    REDIS: true,

    MONITORING: isBetaUser,

    POSTGRESPRO() {
        return isBetaUser() || isUserInSegment(['r1eilx', '1lf2pbk']);
    },

    CAN_USE_MARKETPLACE_TEST_BUCKET:
        !Environment.isProductionHost || isBetaUser,

    MONITORING_DISK: true,

    PROMO_POPUP: false,

    BLACK_FRIDAY: true,

    RETURN_FROM_PC_POPUP: false,

    KARBOR_BACKUPS: true,

    LOAD_BALANCER: true,

    NETWORKS: true,

    VOLUMES: true,

    MANILA: true,

    ACCESS: false,

    CHANGE_ROOT_DISK: true,

    INT_NET1_MESSAGE: true,

    TFA: true,

    CLOUD_STORAGE_LIFECYCLE_DATE: isAlphaUser,

    VPN_TUNNELS: true,

    IMAGES: true,

    KEY_PAIRS: true,

    CLICKHOUSE_CLUSTER: true,

    SERVER_CONSOLE: true,

    CLOUD_SERVERS_VOLUME_EXTENDING: true,

    PHONE_VERIFICATION: true,

    REGISTRATION_SINGLE_PASSWORD: true,

    SMS_VERIFICATION() {
        return true;
        // return jigurda('sms_verification', getCurrentUserEmail(), 50);
    },

    SIGNUP_WITH_PHONE: false,

    SPECIAL_SERVICES: true,

    CLOUD_STORAGE_SERVICE() {
        return checkServiceAvailability({
            serviceAliasForWhiteLabelSettings: 'hotbox',
        });
    },

    CLOUD_SERVERS_SERVICE() {
        return checkServiceAvailability({
            serviceAliasForWhiteLabelSettings: 'infra',
        });
    },

    CLOUD_CONTAINERS_SERVICE() {
        return checkServiceAvailability({
            serviceAliasForWhiteLabelSettings: 'containers',
        });
    },

    CLOUD_DATABASES_SERVICE() {
        return checkServiceAvailability({
            serviceAliasForWhiteLabelSettings: 'databases',
        });
    },

    // todo: сделать проверку сервиса когда будет приходить сервис
    ANALYTIC_DATABASES_SERVICE() {
        /* return (
            isBetaUser() ||
            isUserInSegment([
                '288tsg',
                'nb5urn',
                'gslf9w',
                '1dvmbci',
                'ja46pf',
                '1kjfjgv',
                '16awldq',
            ])
        ); */
        return checkBigDataServiceAvailability();
    },

    CLOUD_BIG_DATA_SERVICE: checkBigDataServiceAvailability,

    CLOUD_GPU_SERVICE() {
        return checkServiceAvailability({
            serviceAliasForWhiteLabelSettings: 'gpu',
        });
    },

    MACHINE_LEARNING_SERVICE() {
        return checkServiceAvailability({
            serviceAliasForWhiteLabelSettings: 'vision',
        });
    },

    MARKETPLACE_SERVICE() {
        return checkServiceAvailability({
            serviceAliasForWhiteLabelSettings: 'marketplace',
        });
    },

    NEW_ACTIVATION_API() {
        return !Environment.isPrivateBuild;
    },

    BILLING_SERVICE() {
        const isAvailableByDefault = true;
        const whiteLabelSettings = BoundWhiteLabelSelectors.getWhiteLabelSettings();

        if (whiteLabelSettings) {
            const { billing_settings } = whiteLabelSettings;

            return billing_settings && billing_settings.enabled === true;
        }

        return isAvailableByDefault;
    },

    WEBHOOKS: true,

    DEBIT_NOTIFY: true,

    ROUTERS: true,
};

export type FeatureName = keyof typeof knownFeatures;

export const features = {
    isEnabled(featureName: FeatureName) {
        let isEnabled = knownFeatures[featureName];

        if (typeof isEnabled === 'function') {
            isEnabled = isEnabled();
        }

        return Boolean(isEnabled);
    },
    isTestUser,
};

export default features;
